import { createContext, useContext, useState, ReactNode } from 'react';

export interface User {
  id: number;
  username: string;
  role: 'Manager' | 'Team Member';
  team: 'Planner' | 'Appointment' | 'Sales Order' | 'Courier' | 'Invoicing' | 'Warehouse' | 'All';
  name: string;
}

interface UserContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  hasAccessToTeam: (team: string) => boolean;
  isManager: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  // For demo purposes, we'll set a default user. In a real app, this would come from authentication
  const [currentUser, setCurrentUser] = useState<User | null>({
    id: 1,
    username: 'demo.user',
    role: 'Team Member',
    team: 'Planner',
    name: 'Demo User'
  });

  const hasAccessToTeam = (team: string): boolean => {
    if (!currentUser) return false;
    
    // Managers have access to all teams
    if (currentUser.role === 'Manager') return true;
    
    // Team members only have access to their own team
    const teamMap: Record<string, string> = {
      'planner': 'Planner',
      'appointment': 'Appointment', 
      'so': 'Sales Order',
      'courier': 'Courier',
      'invoicing': 'Invoicing',
      'warehouse': 'Warehouse'
    };
    
    return teamMap[team] === currentUser.team || team === currentUser.team;
  };

  const isManager = (): boolean => {
    return currentUser?.role === 'Manager';
  };

  return (
    <UserContext.Provider value={{
      currentUser,
      setCurrentUser,
      hasAccessToTeam,
      isManager
    }}>
      {children}
    </UserContext.Provider>
  );
};